import { FunctionComponent, useState } from 'react'
import { css } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'

import { InputValues } from '@/components/TipSelect/InputCurrency'
import { useDebounce } from '@/libs/helpers/hooks/useDebounce'
import { calcTipAmount } from '@/libs/helpers/utils'
import { Grid, Text } from '@/tbui'

const TIP_INPUT_DEBOUNCE_MS = 800

enum CustomTipType {
  DOLLAR = 'dollar',
  PERCENTAGE = 'percentage',
}

const wrapperStyle = css`
  margin-top: 12px;
`

const rowStyle = css`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: stretch;
`

const toggleGroupStyle = (theme: Theme): string => css`
  display: inline-flex;
  border: 1px solid ${theme.palette.GRAY_2};
  border-radius: ${theme.shape.RADIUS};
  overflow: hidden;
`

const toggleButtonStyle = (theme: Theme, active: boolean): string => css`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 8px 14px;
  min-width: 40px;
  font-size: 14px;
  height: 100%;
  background: ${active ? theme.palette.PRIMARY : theme.palette.WHITE};
  color: ${active ? theme.palette.WHITE : theme.palette.TEXT};
  font-weight: ${active ? 600 : 400};
  transition: background 0.15s ease;
`

const inputStyle = (theme: Theme): string => css`
  width: 100%;
  border: 1px solid ${theme.palette.GRAY_2};
  border-radius: ${theme.shape.RADIUS};
  padding: 8px 12px;
  font-size: 14px;
  outline: none;
  height: 2.75rem;
  background: ${theme.palette.WHITE};
  color: ${theme.palette.TEXT};

  &:focus {
    border-color: ${theme.palette.PRIMARY};
  }
`

interface CustomTipInputProps {
  /** Dollar amount that seeds the $ input (e.g. "5.77" or "0.00"). */
  seedDollar: string
  /** Percent string that seeds the % input (e.g. "20" or ""). */
  seedPercent: string
  totalAfterTax: string
  onChange: (tipAmount: string) => void
}

const CustomTipInput: FunctionComponent<CustomTipInputProps> = ({
  seedDollar,
  seedPercent,
  totalAfterTax,
  onChange,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const [tipType, setTipType] = useState<CustomTipType>(CustomTipType.DOLLAR)
  const [dollarValue, setDollarValue] = useState<string>(seedDollar)
  const [percentValue, setPercentValue] = useState<string>(seedPercent)

  const debouncedOnChange = useDebounce(onChange, TIP_INPUT_DEBOUNCE_MS)

  const handleSelectType = (nextType: CustomTipType): void => {
    if (nextType === tipType) {
      return
    }
    setTipType(nextType)
    if (nextType === CustomTipType.DOLLAR) {
      onChange(dollarValue === '' ? '0.00' : dollarValue)
    } else {
      onChange(percentValue === '' ? '0.00' : calcTipAmount(totalAfterTax, percentValue))
    }
  }

  const handleDollarChange = (values: InputValues): void => {
    const { value } = values
    setDollarValue(value)
    debouncedOnChange(value === '' ? '0.00' : value)
  }

  const handlePercentChange = (values: InputValues): void => {
    const { value } = values
    setPercentValue(value)
    debouncedOnChange(value === '' ? '0.00' : calcTipAmount(totalAfterTax, value))
  }

  const helperKey =
    tipType === 'dollar'
      ? 'checkout.payment.custom_tip_helper_dollar'
      : 'checkout.payment.custom_tip_helper_percentage'

  return (
    <Grid className={wrapperStyle} data-test="custom-tip-input">
      <Text type="P" color="TEXT" bold m="8px 0">
        {t('checkout.payment.custom_tip_amount')}
      </Text>
      <Grid className={rowStyle}>
        <Grid className={toggleGroupStyle(theme)} role="group" aria-label="Custom tip type">
          <button
            type="button"
            className={toggleButtonStyle(theme, tipType === CustomTipType.DOLLAR)}
            onClick={(): void => handleSelectType(CustomTipType.DOLLAR)}
            data-test="custom-tip-dollar-toggle"
            aria-pressed={tipType === CustomTipType.DOLLAR}
          >
            $
          </button>
          <button
            type="button"
            className={toggleButtonStyle(theme, tipType === CustomTipType.PERCENTAGE)}
            onClick={(): void => handleSelectType(CustomTipType.PERCENTAGE)}
            data-test="custom-tip-percentage-toggle"
            aria-pressed={tipType === CustomTipType.PERCENTAGE}
          >
            %
          </button>
        </Grid>
        {tipType === CustomTipType.DOLLAR ? (
          <NumericFormat
            placeholder="$0.00"
            value={dollarValue}
            isAllowed={(inputValues: InputValues): boolean => (inputValues.floatValue ?? 0) <= 1000}
            onValueChange={handleDollarChange}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator
            data-test="custom-tip-dollar-input"
            name="custom-tip-dollar-input"
            prefix="$"
            className={inputStyle(theme)}
          />
        ) : (
          <NumericFormat
            placeholder="0%"
            value={percentValue}
            isAllowed={(values: InputValues): boolean => values.value.length <= 2}
            onValueChange={handlePercentChange}
            allowNegative={false}
            decimalScale={0}
            data-test="custom-tip-percentage-input"
            name="custom-tip-percentage-input"
            suffix=" %"
            className={inputStyle(theme)}
          />
        )}
      </Grid>
      <Text type="P" color="GRAY_3" m="10px 0 0 0">
        {t(helperKey)}
      </Text>
    </Grid>
  )
}

export default CustomTipInput
